import { z } from "zod";
import { EntityType } from "../entityType.js";
import { pluralize } from "../utils/common.js";

export const ActorKind = {
  /** A person acting directly in the app. */
  App: "app",
  /** Acting through a scoped API key. We don't assume it's an autonomous agent. */
  ApiKey: "api-key",
  /** Internal assistant, acting on behalf of a specific human in a conversation. */
  Assistant: "assistant",
  /** A connected app acting through an OAuth connection the user consented to. */
  OauthClient: "oauth-client",
  /** Automated action (jobs, crons). */
  System: "system",
} as const;

export type ActorKind = (typeof ActorKind)[keyof typeof ActorKind];

export const actorKinds = Object.values(ActorKind);

/**
 * Derives the actor kind from the created-by columns an entity row carries.
 */
export function actorKindFromRecord(record: {
  createdByAssistant?: boolean | null;
  createdByApiKeyId?: string | null;
  createdByOAuthClientId?: string | null;
}): ActorKind {
  if (record.createdByAssistant) return ActorKind.Assistant;
  if (record.createdByOAuthClientId) return ActorKind.OauthClient;
  if (record.createdByApiKeyId) return ActorKind.ApiKey;
  return ActorKind.App;
}

export const activityVerbs = [
  "fact.created",
  "fact.updated",
  "fact.bulk_created",
  "fact.bulk_removed",
  "fact.all_removed",
  "fact.permissions_updated",

  "task.created",
  "task.updated",
  "task.completed",
  "task.assignees_updated",
  "task.tags_updated",
  "task.contacts_updated",
  "task.deleted",
  "task.bulk_created",
  "task.bulk_completed",
  "task.positions_updated",

  "note.created",
  "note.updated",
  "note.deleted",
  "note.privacy_updated",

  "asset.created",
  "asset.version_created",
  "asset.version_updated",
  "asset.deleted",
  "asset.tags_updated",
  "asset.privacy_updated",

  "contact.created",
  "contact.updated",
  "contact.deleted",
  "contact.privacy_updated",
  "contact.tag_assigned",
  "contact.tags_assigned",
  "contact.tag_removed",
  "contact.tags_removed",
  "contact.memo_added",
  "contact.memo_updated",
  "contact.memo_deleted",
  "contact.memo_privacy_updated",
  "contact.company_role_added",
  "contact.company_role_updated",
  "contact.company_role_deleted",
  "contact.bulk_created",
  "contact.bulk_deleted",
  "contact.imported",

  "stack.created",
  "stack.updated",
  "stack.added_to_organization",
  "stack.removed_from_organization",
  "block.created",
  "block.removed",

  "tag.created",
  "tag.updated",
  "tag.deleted",
] as const;

export type ActivityVerb = (typeof activityVerbs)[number];

export const ActivityMetadataSchema = z
  .object({
    count: z.number().optional(),
    tagName: z.string().optional(),
    stackSlug: z.string().optional(),
    removedFactsCount: z.number().optional(),
    /** Which route the credential acted through, only filled for api-key for now. */
    via: z.enum(["mcp", "rest"]).optional(),
  })
  .catchall(z.unknown());

export type ActivityMetadata = z.infer<typeof ActivityMetadataSchema>;

export const ActivityEventSchema = z.object({
  id: z.string(),
  createdAt: z.date(),
  actorKind: z.enum(actorKinds),
  actorLabel: z.string(),
  actorIdentityId: z.string(),
  actorApiKeyId: z.string().nullable(),
  actorOAuthClientId: z.string().nullable(),
  verb: z.string(),
  entityType: z.string().nullable(),
  entityId: z.string().nullable(),
  entityLabel: z.string().nullable(),
  blockPath: z.string().nullable(),
  metadata: ActivityMetadataSchema,
});

export type ActivityEvent = z.infer<typeof ActivityEventSchema>;

export const ActivityListInputSchema = z.object({
  cursor: z.object({ createdAt: z.date(), id: z.string() }).optional(),
  limit: z.number().int().min(1).max(50).default(25),
  actorKind: z.array(z.enum(actorKinds)).min(1).optional(),
  actorIdentityId: z.string().optional(),
  entityType: z.array(z.enum(EntityType)).min(1).optional(),
});

export type ActivityListInput = z.infer<typeof ActivityListInputSchema>;

export interface RecordActivityInput {
  verb: ActivityVerb;
  entityType: EntityType;
  entityId?: string;
  entityLabel?: string | null;
  blockId?: string | null;
  blockPath?: string | null;
  entityPrivate?: boolean;
  entityCreatorId?: string | null;
  isAssistant?: boolean;
  isSystem?: boolean;
  metadata?: ActivityMetadata;
}

interface VerbPhrasing {
  action: string;
  noun: string;
  /** Where the tag name/count sits in the sentence, if this verb has one. */
  tagPosition?: "suffix" | "prefix" | "suffix-count" | "prefix-count";
  /** Renders from metadata.count instead of entityLabel. */
  bulk?: boolean;
}

const verbPhrasing: Record<ActivityVerb, VerbPhrasing> = {
  "fact.created": { action: "added", noun: "fact" },
  "fact.updated": { action: "updated", noun: "fact" },
  "fact.bulk_created": { action: "added", noun: "fact", bulk: true },
  "fact.bulk_removed": { action: "removed", noun: "fact", bulk: true },
  "fact.all_removed": { action: "removed all", noun: "fact", bulk: true },
  "fact.permissions_updated": {
    action: "updated fact permissions for",
    noun: "role",
  },

  "task.created": { action: "created", noun: "task" },
  "task.updated": { action: "updated", noun: "task" },
  "task.completed": { action: "completed", noun: "task" },
  "task.assignees_updated": { action: "changed assignees on", noun: "task" },
  "task.tags_updated": { action: "changed tags on", noun: "task" },
  "task.contacts_updated": { action: "changed contacts on", noun: "task" },
  "task.deleted": { action: "deleted", noun: "task" },
  "task.bulk_created": { action: "created", noun: "task", bulk: true },
  "task.bulk_completed": { action: "completed", noun: "task", bulk: true },
  "task.positions_updated": { action: "reordered", noun: "task", bulk: true },

  "note.created": { action: "added", noun: "note" },
  "note.updated": { action: "updated", noun: "note" },
  "note.deleted": { action: "deleted", noun: "note" },
  "note.privacy_updated": { action: "changed privacy on", noun: "note" },

  "asset.created": { action: "created", noun: "asset" },
  "asset.version_created": {
    action: "published a new version of",
    noun: "asset",
  },
  "asset.version_updated": { action: "updated a version of", noun: "asset" },
  "asset.deleted": { action: "deleted", noun: "asset" },
  "asset.tags_updated": { action: "changed tags on", noun: "asset" },
  "asset.privacy_updated": { action: "changed privacy on", noun: "asset" },

  "contact.created": { action: "added", noun: "contact" },
  "contact.updated": { action: "updated", noun: "contact" },
  "contact.deleted": { action: "deleted", noun: "contact" },
  "contact.privacy_updated": {
    action: "changed privacy on",
    noun: "contact",
  },
  "contact.tag_assigned": {
    action: "tagged",
    noun: "contact",
    tagPosition: "suffix",
  },
  "contact.tags_assigned": {
    action: "tagged",
    noun: "contact",
    tagPosition: "suffix-count",
  },
  "contact.tag_removed": {
    action: "removed tag",
    noun: "contact",
    tagPosition: "prefix",
  },
  "contact.tags_removed": {
    action: "removed",
    noun: "contact",
    tagPosition: "prefix-count",
  },
  "contact.memo_added": { action: "added a memo to", noun: "contact" },
  "contact.memo_updated": { action: "updated a memo on", noun: "contact" },
  "contact.memo_deleted": { action: "deleted a memo from", noun: "contact" },
  "contact.memo_privacy_updated": {
    action: "changed memo privacy on",
    noun: "contact",
  },
  "contact.company_role_added": {
    action: "added a company role to",
    noun: "contact",
  },
  "contact.company_role_updated": {
    action: "updated a company role on",
    noun: "contact",
  },
  "contact.company_role_deleted": {
    action: "removed a company role from",
    noun: "contact",
  },
  "contact.bulk_created": { action: "created", noun: "contact", bulk: true },
  "contact.bulk_deleted": { action: "deleted", noun: "contact", bulk: true },
  "contact.imported": { action: "imported", noun: "contact", bulk: true },

  "stack.created": { action: "created", noun: "stack" },
  "stack.updated": { action: "updated", noun: "stack" },
  "stack.added_to_organization": {
    action: "added",
    noun: "stack",
  },
  "stack.removed_from_organization": {
    action: "removed",
    noun: "stack",
  },
  "block.created": { action: "created", noun: "block" },
  "block.removed": { action: "removed", noun: "block" },

  "tag.created": { action: "created", noun: "tag" },
  "tag.updated": { action: "updated", noun: "tag" },
  "tag.deleted": { action: "deleted", noun: "tag" },
};

export interface ActivitySummaryParts {
  /** Actor + verb + any preposition, up to the object. */
  lead: string;
  /** The thing acted on, meant to be emphasized. */
  object: string;
  /** Trailing text after the object, if any. */
  tail: string;
}

/**
 * Set withActor to false when the actor is already named nearby, so the
 * sentence starts on the verb instead of repeating the name.
 */
export function activitySummaryParts(
  event: ActivityEvent,
  { withActor = true }: { withActor?: boolean } = {},
): ActivitySummaryParts {
  const phrasing = (verbPhrasing as Record<string, VerbPhrasing>)[event.verb];
  const actor = withActor ? `${event.actorLabel} ` : "";

  if (!phrasing) {
    return { lead: `${actor}did `, object: event.verb, tail: "" };
  }

  if (phrasing.bulk) {
    const count = event.metadata.count ?? 0;
    return {
      lead: `${actor}${phrasing.action} `,
      object: pluralize(count, phrasing.noun),
      tail: "",
    };
  }

  const object = event.entityLabel
    ? `${phrasing.noun} "${event.entityLabel}"`
    : `a ${phrasing.noun}`;

  if (
    phrasing.tagPosition === "suffix-count" ||
    phrasing.tagPosition === "prefix-count"
  ) {
    const count = event.metadata.count ?? 0;
    const tags = pluralize(count, "tag");

    return phrasing.tagPosition === "prefix-count"
      ? { lead: `${actor}${phrasing.action} ${tags} from `, object, tail: "" }
      : { lead: `${actor}${phrasing.action} `, object, tail: ` with ${tags}` };
  }

  const tagName = phrasing.tagPosition
    ? (event.metadata.tagName ?? null)
    : null;

  if (tagName && phrasing.tagPosition === "prefix") {
    return {
      lead: `${actor}${phrasing.action} "${tagName}" from `,
      object,
      tail: "",
    };
  }

  const removedFactsCount = event.metadata.removedFactsCount ?? 0;

  return {
    lead: `${actor}${phrasing.action} `,
    object,
    tail: tagName
      ? ` with "${tagName}"`
      : removedFactsCount > 0
        ? ` and ${pluralize(removedFactsCount, "fact")}`
        : "",
  };
}

export function renderActivitySummary(
  event: ActivityEvent,
  options: { withActor?: boolean } = {},
): string {
  const { lead, object, tail } = activitySummaryParts(event, options);
  return `${lead}${object}${tail}`;
}
